/**
 * The sounds a computer makes while it starts something.
 *
 * Synthesised rather than played from files, for three reasons: there
 * is nothing to download on a school connection, nothing to licence,
 * and the production CSP allows no external media anyway.
 *
 * Everything here is deliberately quiet and short. This runs in a
 * classroom, where thirty machines making noises at once is the
 * difference between a useful lesson and an unusable one.
 *
 * Nothing is created until the learner turns sound on. That is partly
 * manners and partly necessity: browsers refuse to start audio that no
 * one asked for, so the AudioContext has to be born inside a click.
 */

const MASTER = 0.14;        // quiet. classrooms.

let ctx = null;

/** Called from a click handler, or the browser will refuse. */
export function wake() {
  if (!ctx) {
    const Ctx = window.AudioContext ?? window.webkitAudioContext;
    if (!Ctx) return false;
    ctx = new Ctx();
  }
  if (ctx.state === 'suspended') ctx.resume();
  return true;
}

export function sleep() {
  if (ctx && ctx.state === 'running') ctx.suspend();
}

/** The shared context, so the music bed can play through the same one. */
export function context() {
  return ctx && ctx.state === 'running' ? ctx : null;
}

export function running() {
  return Boolean(ctx && ctx.state === 'running');
}

/** A short burst of noise, for anything mechanical. */
function noise(seconds) {
  const frames = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  return src;
}

function envelope(attack, hold, release, peak = 1) {
  const t = ctx.currentTime;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(MASTER * peak, t + attack);
  g.gain.setValueAtTime(MASTER * peak, t + attack + hold);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + hold + release);
  g.connect(ctx.destination);
  return { gain: g, stopAt: t + attack + hold + release };
}

const VOICES = {
  /** A mouse click: short, dry, high. */
  click() {
    const { gain, stopAt } = envelope(0.001, 0.004, 0.04, 0.9);
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(1800, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(600, stopAt);
    osc.connect(gain);
    osc.start();
    osc.stop(stopAt);
  },





  /** Power flooding in: a low swell with a bright edge on top. */
  powerUp() {
    const { gain, stopAt } = envelope(0.04, 0.3, 0.35, 0.9);
    const low = ctx.createOscillator();
    low.type = 'sawtooth';
    low.frequency.setValueAtTime(48, ctx.currentTime);
    low.frequency.exponentialRampToValueAtTime(150, stopAt);
    const tame = ctx.createBiquadFilter();
    tame.type = 'lowpass';
    tame.frequency.setValueAtTime(300, ctx.currentTime);
    tame.frequency.exponentialRampToValueAtTime(2600, stopAt);
    low.connect(tame).connect(gain);
    low.start();
    low.stop(stopAt);
  },

  /** The processor coming to life: a short upward electronic figure. */
  cpuWake() {
    [330, 440, 660].forEach((hz, i) => {
      const t = ctx.currentTime + i * 0.07;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(MASTER * 0.55, t + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
      g.connect(ctx.destination);
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = hz;
      osc.connect(g);
      osc.start(t);
      osc.stop(t + 0.24);
    });
  },

  /** Data on the move: a quiet shimmer, easy to sit under narration. */
  dataFlow() {
    const { gain, stopAt } = envelope(0.05, 0.2, 0.25, 0.35);
    const src = noise(0.5);
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = 14;
    band.frequency.setValueAtTime(1600, ctx.currentTime);
    band.frequency.linearRampToValueAtTime(3200, stopAt);
    src.connect(band).connect(gain);
    src.start();
    src.stop(stopAt);
  },

  /** One piece of hardware found and working. */
  confirmBeep() {
    const { gain, stopAt } = envelope(0.004, 0.03, 0.09, 0.55);
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1320, ctx.currentTime);
    osc.connect(gain);
    osc.start();
    osc.stop(stopAt);
  },

  /** Something important arriving. Deliberately the brightest cue here. */
  reveal() {
    [523.25, 659.25, 783.99, 1046.5].forEach((hz, i) => {
      const t = ctx.currentTime + i * 0.075;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(MASTER * (0.7 - i * 0.08), t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.7);
      g.connect(ctx.destination);
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = hz;
      osc.connect(g);
      osc.start(t);
      osc.stop(t + 0.72);
    });
  },

  /** Something large being loaded: a slow rise that resolves. */
  loadRise() {
    const { gain, stopAt } = envelope(0.06, 0.5, 0.25, 0.5);
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(160, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(640, stopAt);
    const tame = ctx.createBiquadFilter();
    tame.type = 'lowpass';
    tame.frequency.value = 1800;
    osc.connect(tame).connect(gain);
    osc.start();
    osc.stop(stopAt);
  },

  /** Signature checked, door closed behind it. */
  lockOk() {
    const { gain, stopAt } = envelope(0.004, 0.02, 0.07, 0.5);
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(760, ctx.currentTime);
    osc.frequency.setValueAtTime(1140, ctx.currentTime + 0.05);
    osc.connect(gain);
    osc.start();
    osc.stop(stopAt);
    // the clunk under it
    const { gain: g2, stopAt: s2 } = envelope(0.002, 0.01, 0.1, 0.45);
    const thud = ctx.createOscillator();
    thud.type = 'sine';
    thud.frequency.setValueAtTime(180, ctx.currentTime + 0.05);
    thud.connect(g2);
    thud.start(ctx.currentTime + 0.05);
    thud.stop(s2 + 0.05);
  },

  /**
   * A quiz passed. The one cheerful noise in here, and the only one
   * allowed to be a tune: a major arpeggio up to the octave, with the
   * last note held.
   */
  fanfare() {
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((hz, i) => {
      const t = ctx.currentTime + i * 0.1;
      const last = i === notes.length - 1;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(MASTER * 0.85, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + (last ? 0.9 : 0.3));
      g.connect(ctx.destination);
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = hz;
      osc.connect(g);
      osc.start(t);
      osc.stop(t + (last ? 0.95 : 0.35));
    });
    // a little sparkle over the top of the last note
    const t = ctx.currentTime + 0.3;
    [1568, 2093, 2637].forEach((hz, i) => {
      const at = t + i * 0.07;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(MASTER * 0.3, at + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.25);
      g.connect(ctx.destination);
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = hz;
      osc.connect(g);
      osc.start(at);
      osc.stop(at + 0.3);
    });
  },

  /** Something is wrong. Low, short, and not alarming enough to scare. */
  warnLow() {
    const { gain, stopAt } = envelope(0.02, 0.18, 0.22, 0.6);
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(196, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(146, stopAt);
    osc.connect(gain);
    osc.start();
    osc.stop(stopAt);
  },

};

export function play(name) {
  if (!ctx || ctx.state !== 'running') return;
  VOICES[name]?.();
}

export const VOICE_NAMES = Object.keys(VOICES);
